/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getFirestore,
  initializeFirestore,
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  or,
  orderBy,
  writeBatch,
  getDocs,
  increment,
  deleteField,
} from "firebase/firestore";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  User,
} from "firebase/auth";
import { getStorage } from "firebase/storage";
import { getMessaging, isSupported as isMessagingSupported, Messaging } from "firebase/messaging";
import { Dupla, GameState, HistoricoRodada, Tournament, SponsorRaffle, SponsorRaffleParticipant } from "../types";
import { CourtItem, UserProfile, OFFICIAL_ADMIN_EMAILS } from "../types/userProfile";
import { StorageService } from "../services/storage/StorageService";
import { STORAGE_KEYS } from "../services/storage/storageKeys";
import { UserProfileRepository } from "../services/storage/repositories/UserProfileRepository";
import { validateCustomCommissionRate } from "./commercialRuleUtils";
import { isFinancialDebugEnabled } from "../services/financial/FinancialModelResolver";
import { isRealCourtBookingDocument } from "../services/booking/BookingStatusService";

// Tenta carregar as configurações do Firebase das variáveis de ambiente expostas ao client (com prefixo VITE_)
const env = (import.meta as any).env || {};

const firebaseConfig = {
  apiKey: env.VITE_FIREBASE_API_KEY || "AIzaSyC3bOnA49vSttiDcAkYx-QD9y-Y6oQHunA",
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN || "rachao-de-volei.firebaseapp.com",
  projectId: env.VITE_FIREBASE_PROJECT_ID || "rachao-de-volei",
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET || "rachao-de-volei.firebasestorage.app",
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID || "1045642165463",
  appId: env.VITE_FIREBASE_APP_ID || "1:1045642165463:web:255ef7377dc02e9a190492",
};

// Verifica se as chaves mínimas necessárias estão presentes para inicializar o Firebase
const isConfigured = !!(
  firebaseConfig.apiKey &&
  firebaseConfig.projectId
);

let app;
let db: any = null;
let auth: any = null;
let storage: any = null;

if (isConfigured) {
  try {
    app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
    try {
      db = initializeFirestore(app, {
        experimentalAutoDetectLongPolling: true,
      });
    } catch {
      db = getFirestore(app);
    }
    auth = getAuth(app);
    try {
      storage = getStorage(app);
    } catch (sErr) {
      console.warn("Firebase Storage não inicializado:", sErr);
    }
    console.log("🔥 Firebase, Auth e Storage inicializados com sucesso!");
  } catch (error) {
    console.error("Erro ao inicializar o Firebase:", error);
  }
} else {
  console.log("ℹ️ Firebase não configurado. Utilizando mecanismo persistente LocalStorage.");
}

let messagingInstance: Messaging | null = null;

export async function getFirebaseMessaging(): Promise<Messaging | null> {
  if (!isFirebaseConfigured() || !app) return null;
  if (messagingInstance) return messagingInstance;
  try {
    const supported = await isMessagingSupported();
    if (supported) {
      messagingInstance = getMessaging(app);
      return messagingInstance;
    }
  } catch (err) {
    console.warn("Firebase Messaging não suportado neste ambiente:", err);
  }
  return null;
}

export { auth, db, storage };

export async function getFirebaseAuthToken(): Promise<string | null> {
  if (!auth?.currentUser) return null;
  try {
    return await auth.currentUser.getIdToken();
  } catch {
    return null;
  }
}

export async function getAuthHeaders(extraHeaders: Record<string, string> = {}): Promise<Record<string, string>> {
  const headers: Record<string, string> = { ...extraHeaders };
  try {
    const token = await getFirebaseAuthToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  } catch {}
  return headers;
}

export function isFirebaseConfigured(): boolean {
  try {
    if (typeof (globalThis as any).__FORCE_FIREBASE_CONFIGURED__ === "boolean") {
      return (globalThis as any).__FORCE_FIREBASE_CONFIGURED__;
    }
    if (typeof process !== "undefined" && (process.env.VITEST === "true" || process.env.NODE_ENV === "test")) {
      return false;
    }
    const configured = !!(firebaseConfig.apiKey && firebaseConfig.projectId);
    return configured && db !== null && auth !== null;
  } catch {
    return false;
  }
}

// --- INTEGRAÇÃO COM GOOGLE AUTH ---

export async function loginWithGoogle(): Promise<User | null> {
  if (!isFirebaseConfigured()) {
    console.warn("Firebase não configurado.");
    return null;
  }
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({
    prompt: "select_account",
  });

  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error: any) {
    const errorCode = error?.code || "";
    // Se o popup foi fechado pelo usuário ou cancelado, loga pacificamente e retorna null
    if (
      errorCode === "auth/popup-closed-by-user" ||
      errorCode === "auth/cancelled-popup-request" ||
      error?.message?.includes("popup-closed-by-user")
    ) {
      console.log("ℹ️ Login com Google cancelado pelo usuário (popup fechado).");
      return null;
    }

    // Se o popup foi bloqueado pelo navegador ou falhou por política de cross-origin, tenta redirect
    if (
      errorCode === "auth/popup-blocked" ||
      errorCode === "auth/operation-not-allowed" ||
      error?.message?.includes("Cross-Origin-Opener-Policy")
    ) {
      console.warn("⚠️ Popup do Google bloqueado ou restrito. Redirecionando para login...");
      try {
        await signInWithRedirect(auth, provider);
        return null;
      } catch (redirectErr) {
        console.error("Erro ao tentar login com Google via redirect:", redirectErr);
        throw redirectErr;
      }
    }

    console.error("Erro ao fazer login com Google:", error);
    throw error;
  }
}

export async function loginWithEmailPassword(email: string, pass: string): Promise<User | null> {
  if (!isFirebaseConfigured() || !auth) {
    console.warn("Firebase Auth não configurado.");
    return null;
  }
  const result = await signInWithEmailAndPassword(auth, email.trim(), pass);
  return result.user;
}

export async function registerWithEmailPassword(email: string, pass: string): Promise<User | null> {
  if (!isFirebaseConfigured() || !auth) {
    console.warn("Firebase Auth não configurado.");
    return null;
  }
  const result = await createUserWithEmailAndPassword(auth, email.trim(), pass);
  return result.user;
}

export async function resetPasswordWithEmail(email: string): Promise<void> {
  if (!isFirebaseConfigured() || !auth) {
    console.warn("Firebase Auth não configurado.");
    return;
  }
  await sendPasswordResetEmail(auth, email.trim());
}

export async function logoutUser(): Promise<void> {
  if (!isFirebaseConfigured()) return;
  const currentUid = auth?.currentUser?.uid;
  if (currentUid) {
    try {
      const { unregisterCurrentDevice } = await import("../services/fcm/fcmService");
      await unregisterCurrentDevice(currentUid);
    } catch (err) {
      console.warn("Falha ao desassociar dispositivo FCM durante logout:", err);
    }
  }
  try {
    await signOut(auth);
  } catch (error) {
    console.error("Erro ao deslogar do Firebase:", error);
    throw error;
  }
}

export function onAuthStateChange(callback: (user: User | null) => void) {
  if (!isFirebaseConfigured()) {
    callback(null);
    return () => {};
  }
  // Processa o resultado de redirecionamento, se houver
  getRedirectResult(auth)
    .then((result) => {
      if (result?.user) {
        console.log("✅ Login concluído via redirect Google:", result.user.email);
      }
    })
    .catch((err) => {
      if (
        err?.code !== "auth/popup-closed-by-user" &&
        err?.code !== "auth/cancelled-popup-request"
      ) {
        console.warn("Informação sobre redirect result:", err?.message || err);
      }
    });

  return onAuthStateChanged(auth, callback);
}

// Auxiliar para remover valores undefined de objetos que serão salvos no Firestore
export function sanitizeForFirestore<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj)) as T;
}

export function sanitizeCourtForFirestore(court: any): any {
  const sanitized = sanitizeForFirestore(court);
  delete sanitized.ownerMercadoPagoAccessToken;
  delete sanitized.ownerMercadoPagoPublicKey;
  delete sanitized.ownerPixKey;
  delete sanitized.ownerPixKeyType;
  delete sanitized.pixKey;
  delete sanitized.pixKeyType;
  delete sanitized.activeCourtCount;
  delete sanitized.secret;
  delete sanitized.accessToken;
  delete sanitized.privateKey;
  return sanitized;
}

// Helper para obter coleção do usuário
function getUserCollectionPath(userId: string, sub: string) {
  return `users/${userId}/${sub}`;
}

// --- INTEGRAÇÃO COM DUPLAS ---

// Escuta em tempo real as duplas
export function subscribeDuplas(
  userId: string | null,
  callback: (duplas: Dupla[]) => void,
  fallbackData: Dupla[]
) {
  if (!isFirebaseConfigured() || !userId || !auth?.currentUser || auth.currentUser.uid !== userId) {
    callback(fallbackData);
    return () => {};
  }

  const path = getUserCollectionPath(userId, "duplas");
  const q = query(collection(db, path), orderBy("createdAt", "asc"));
  return onSnapshot(
    q,
    (snapshot) => {
      const duplas: Dupla[] = [];
      snapshot.forEach((doc) => {
        duplas.push({ id: doc.id, ...doc.data() } as Dupla);
      });
      
      // Se a coleção estiver totalmente vazia no Firestore, popula com os dados padrões apenas uma vez (migração)
      const migrationKey = `rachao_migrated_to_firestore_${userId}`;
      const hasMigrated = StorageService.getRaw(migrationKey);
      if (duplas.length === 0 && fallbackData.length > 0 && !hasMigrated) {
        StorageService.setRaw(migrationKey, "true");
        seedDefaultDuplas(userId, fallbackData);
      } else {
        StorageService.setRaw(migrationKey, "true");
        callback(duplas);
      }
    },
    (error) => {
      console.error("Erro na escuta de duplas:", error);
      callback(fallbackData);
    }
  );
}

// Salva ou atualiza uma dupla no Firestore
export async function saveDuplaFirestore(userId: string | null, dupla: Dupla, organizerUserId?: string) {
  if (!isFirebaseConfigured() || !auth?.currentUser) return;
  try {
    const cleanData = sanitizeForFirestore(dupla);
    if (userId) {
      const path = getUserCollectionPath(userId, "duplas");
      const docRef = doc(db, path, dupla.id);
      await setDoc(docRef, cleanData, { merge: true });
    }
    if (organizerUserId && organizerUserId !== userId) {
      const orgPath = getUserCollectionPath(organizerUserId, "duplas");
      const orgDocRef = doc(db, orgPath, dupla.id);
      await setDoc(orgDocRef, cleanData, { merge: true });
    }
  } catch (error) {
    console.error("Erro ao salvar dupla no Firestore:", error);
  }
}

// Exclui uma dupla no Firestore
export async function deleteDuplaFirestore(userId: string | null, id: string) {
  if (!isFirebaseConfigured() || !userId || !auth?.currentUser) return;
  try {
    const path = getUserCollectionPath(userId, "duplas");
    const docRef = doc(db, path, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Erro ao deletar dupla no Firestore:", error);
  }
}

// Popula o Firestore com as duplas iniciais padrão se estiver vazio
async function seedDefaultDuplas(userId: string, defaultDuplas: Dupla[]) {
  if (!isFirebaseConfigured()) return;
  try {
    const path = getUserCollectionPath(userId, "duplas");
    const batch = writeBatch(db);
    defaultDuplas.forEach((d) => {
      const docRef = doc(db, path, d.id);
      batch.set(docRef, sanitizeForFirestore(d));
    });
    await batch.commit();
    console.log(`🌱 Coleção de duplas semeada para o usuário ${userId}.`);
  } catch (error) {
    console.error("Erro ao semear duplas padrões no Firestore:", error);
  }
}


// --- INTEGRAÇÃO COM ESTADO DO JOGO ---

// Escuta em tempo real o estado do jogo atual
export function subscribeGameState(
  userId: string | null,
  callback: (state: GameState) => void,
  fallbackState: GameState
) {
  if (!isFirebaseConfigured() || !userId || !auth?.currentUser || auth.currentUser.uid !== userId) {
    callback(fallbackState);
    return () => {};
  }

  const path = getUserCollectionPath(userId, "gameState");
  const docRef = doc(db, path, "atual");
  return onSnapshot(
    docRef,
    (docSnap) => {
      if (docSnap.exists()) {
        callback(docSnap.data() as GameState);
      } else {
        // Se o documento de estado não existir no Firestore, semeia apenas se o usuário estiver autenticado e for o dono
        if (auth?.currentUser && auth.currentUser.uid === userId) {
          saveGameStateFirestore(userId, fallbackState);
        }
        callback(fallbackState);
      }
    },
    (error) => {
      console.error("Erro ao escutar estado do jogo:", error);
      callback(fallbackState);
    }
  );
}

// Salva o estado do jogo no Firestore
export async function saveGameStateFirestore(userId: string | null, state: GameState) {
  if (!isFirebaseConfigured() || !userId || !auth?.currentUser) return;
  try {
    const path = getUserCollectionPath(userId, "gameState");
    const docRef = doc(db, path, "atual");
    const enrichedState = {
      ...state,
      organizerUserId: state.organizerUserId || userId || auth.currentUser.uid,
      organizerId: state.organizerId || userId || auth.currentUser.uid,
    };
    await setDoc(docRef, sanitizeForFirestore(enrichedState));
  } catch (error) {
    console.error("Erro ao salvar estado do jogo no Firestore:", error);
  }
}


// --- INTEGRAÇÃO COM HISTÓRICO ---

// Escuta em tempo real o histórico de partidas
export function subscribeHistorico(
  userId: string | null,
  callback: (historico: HistoricoRodada[]) => void,
  fallbackHistorico: HistoricoRodada[]
) {
  if (!isFirebaseConfigured() || !userId || !auth?.currentUser || auth.currentUser.uid !== userId) {
    callback(fallbackHistorico);
    return () => {};
  }

  const path = getUserCollectionPath(userId, "historico");
  const q = query(collection(db, path), orderBy("rodada", "asc"));
  return onSnapshot(
    q,
    (snapshot) => {
      const historico: HistoricoRodada[] = [];
      snapshot.forEach((doc) => {
        historico.push({ id: doc.id, ...doc.data() } as HistoricoRodada);
      });
      callback(historico);
    },
    (error) => {
      console.error("Erro ao escutar histórico de partidas:", error);
      callback(fallbackHistorico);
    }
  );
}

// Adiciona um item ao histórico no Firestore
export async function addHistoricoFirestore(userId: string | null, item: HistoricoRodada) {
  if (!isFirebaseConfigured() || !userId || !auth?.currentUser) return;
  try {
    const path = getUserCollectionPath(userId, "historico");
    const docRef = doc(db, path, item.id);
    await setDoc(docRef, sanitizeForFirestore(item));
  } catch (error) {
    console.error("Erro ao adicionar histórico no Firestore:", error);
  }
}

// Limpa o histórico no Firestore
export async function clearHistoricoFirestore(userId: string | null) {
  if (!isFirebaseConfigured() || !userId || !auth?.currentUser) return;
  try {
    const path = getUserCollectionPath(userId, "historico");
    const querySnapshot = await getDocs(collection(db, path));
    const batch = writeBatch(db);
    querySnapshot.forEach((doc) => {
      batch.delete(doc.ref);
    });
    await batch.commit();
    console.log("🧹 Histórico excluído no Firestore com sucesso.");
  } catch (error) {
    console.error("Erro ao limpar histórico no Firestore:", error);
  }
}


// --- INTEGRAÇÃO COM TORNEIOS ---

// Escuta em tempo real os torneios com Firestore como FONTE CANÔNICA DE VERDADE
export function subscribeTorneios(
  userId: string | null,
  callback: (torneios: Tournament[]) => void,
  fallbackTorneios: Tournament[] = []
) {
  if (!isFirebaseConfigured()) {
    callback(fallbackTorneios);
    return () => {};
  }

  const userTorneiosMap = new Map<string, Tournament>();
  const publicTorneiosMap = new Map<string, Tournament>();
  let hasReceivedFirestoreSnapshot = false;

  const emitMerged = () => {
    const combinedMap = new Map<string, Tournament>();

    if (!hasReceivedFirestoreSnapshot) {
      // Estado inicial transitório antes do primeiro snapshot do Firestore
      (fallbackTorneios || []).forEach((t) => {
        if (t && t.id) combinedMap.set(t.id, t);
      });
    } else {
      // FIRESTORE = FONTE DE VERDADE CANÔNICA
      // 1. Popula com os torneios públicos do Firestore
      publicTorneiosMap.forEach((t, id) => {
        if (t.status !== "excluido") {
          combinedMap.set(id, t);
        }
      });

      // 2. Sobrescreve com a coleção do organizador master (documento privado com dados completos)
      userTorneiosMap.forEach((t, id) => {
        if (t.status === "excluido") {
          combinedMap.delete(id);
          return;
        }
        
        const publicDoc = combinedMap.get(id);
        if (!publicDoc) {
          combinedMap.set(id, t);
        } else {
          // O documento do organizador tem precedência canônica completa
          combinedMap.set(id, {
            ...publicDoc,
            ...t,
            sponsors: t.sponsors !== undefined ? t.sponsors : (publicDoc.sponsors || []),
            gifts: t.gifts !== undefined ? t.gifts : (publicDoc.gifts || []),
            sponsorRaffles: t.sponsorRaffles !== undefined ? t.sponsorRaffles : (publicDoc.sponsorRaffles || []),
            inscritosAtletas: (publicDoc.inscritosAtletas?.length || 0) > (t.inscritosAtletas?.length || 0) ? publicDoc.inscritosAtletas : (t.inscritosAtletas || []),
            equipes: (publicDoc.equipes?.length || 0) > (t.equipes?.length || 0) ? publicDoc.equipes : (t.equipes || []),
          });
        }
      });
    }

    const list = Array.from(combinedMap.values());
    // Ordena por data de criação mais recente
    list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

    // Atualiza cache local sincronizado com a verdade canônica do Firestore
    if (hasReceivedFirestoreSnapshot) {
      try {
        StorageService.set(STORAGE_KEYS.RACHAO_TORNEIOS, list);
      } catch (e) {}
    }

    callback(list);
  };

  const unsubs: Array<() => void> = [];

  // Escuta coleção do usuário (se houver userId e usuário autenticado correspondente)
  if (userId && auth?.currentUser && auth.currentUser.uid === userId) {
    try {
      const path = getUserCollectionPath(userId, "torneios");
      const q = query(collection(db, path));
      const unsubUser = onSnapshot(
        q,
        (snapshot) => {
          hasReceivedFirestoreSnapshot = true;
          userTorneiosMap.clear();
          snapshot.forEach((doc) => {
            userTorneiosMap.set(doc.id, { id: doc.id, ...doc.data() } as Tournament);
          });
          emitMerged();
        },
        (error) => {
          console.error("Erro ao escutar torneios do usuário:", error);
          hasReceivedFirestoreSnapshot = true;
          emitMerged();
        }
      );
      unsubs.push(unsubUser);
    } catch (e) {
      console.error("Erro ao configurar listener de torneios do usuário:", e);
    }
  }

  // Escuta coleção pública global de torneios
  try {
    const publicQ = query(collection(db, "public_tournaments"));
    const unsubPublic = onSnapshot(
      publicQ,
      (snapshot) => {
        hasReceivedFirestoreSnapshot = true;
        publicTorneiosMap.clear();
        snapshot.forEach((doc) => {
          publicTorneiosMap.set(doc.id, { id: doc.id, ...doc.data() } as Tournament);
        });
        emitMerged();
      },
      (error) => {
        console.error("Erro ao escutar torneios públicos:", error);
        hasReceivedFirestoreSnapshot = true;
        emitMerged();
      }
    );
    unsubs.push(unsubPublic);
  } catch (e) {
    console.error("Erro ao configurar listener de torneios públicos:", e);
  }

  // Emite imediatamente com os dados locais antes do Firestore responder
  emitMerged();

  return () => {
    unsubs.forEach((u) => u());
  };
}

// Auxiliar para sanitizar dados públicos do torneio (remove telefones, e-mails e CPFs de participantes dos sorteios e prêmios)
export function sanitizePublicTournamentData(torneio: Tournament): Tournament {
  const sanitized = JSON.parse(JSON.stringify(torneio)) as Tournament;
  
  // Remove credentials and PII
  if ('organizerMercadoPagoAccessToken' in sanitized) delete sanitized.organizerMercadoPagoAccessToken;
  if ('organizerMercadoPagoPublicKey' in sanitized) delete sanitized.organizerMercadoPagoPublicKey;
  if ('organizerCpf' in sanitized) delete sanitized.organizerCpf;

  // Sanitiza participantes e ganhadores de sponsorRaffles para o documento público
  if (Array.isArray(sanitized.sponsorRaffles)) {
    sanitized.sponsorRaffles = sanitized.sponsorRaffles.map((raffle) => {
      const safeParticipants = (raffle.participants || []).map((p) => ({
        id: p.id,
        name: p.name,
        userId: p.userId || undefined,
        createdAt: p.createdAt,
        // Remove phone, email, cpf do documento público
      })) as SponsorRaffleParticipant[];

      const safePrizes = (raffle.prizes || []).map((prize) => {
        if (!prize.winner) return prize;
        return {
          ...prize,
          winner: {
            participantId: prize.winner.participantId,
            participantName: prize.winner.participantName,
            drawnAt: prize.winner.drawnAt,
            // Remove participantPhone e participantEmail do documento público
          },
        };
      });

      return {
        ...raffle,
        participants: safeParticipants,
        prizes: safePrizes,
      };
    });
  }

  // Sanitiza raffleParticipants legado caso exista
  if (Array.isArray(sanitized.raffleParticipants)) {
    sanitized.raffleParticipants = sanitized.raffleParticipants.map((p) => ({
      id: p.id,
      name: p.name,
      phone: "",
      createdAt: p.createdAt || new Date().toISOString(),
    }));
  }

  return sanitized;
}

// Salva ou atualiza um torneio no Firestore (usuário, organizador e global público)
// --- INSCRIÇÃO PÚBLICA DE TORNEIOS ---
export async function registerPublicTournament(tournamentId: string, updatedInscritos: any[], updatedEquipes: string[]): Promise<boolean> {
  if (!isFirebaseConfigured() || !db) return false;
  try {
    const publicDocRef = doc(db, "public_tournaments", tournamentId);
    await updateDoc(publicDocRef, {
      inscritosAtletas: updatedInscritos,
      equipes: updatedEquipes,
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (err) {
    console.error("Erro ao salvar inscrição no public_tournaments:", err);
    throw err;
  }
}

export async function saveTorneioFirestore(userId: string | null, torneio: Tournament): Promise<boolean> {
  if (!isFirebaseConfigured() || !db) return false;
  let success = false;
  try {
    const cleanData = sanitizeForFirestore(torneio);
    const publicCleanData = sanitizeForFirestore(sanitizePublicTournamentData(torneio));

    // 1. Salva/atualiza na coleção pública com dados públicos sanitizados (sem dados pessoais)
    try {
      const publicDocRef = doc(db, "public_tournaments", torneio.id);
      await setDoc(publicDocRef, publicCleanData, { merge: true });
      success = true;
    } catch (pubErr) {
      console.warn("Aviso ao salvar em public_tournaments:", pubErr);
    }

    // 2. Se houver userId logado, salva na subcoleção do usuário
    if (userId) {
      try {
        const path = getUserCollectionPath(userId, "torneios");
        const docRef = doc(db, path, torneio.id);
        await setDoc(docRef, cleanData, { merge: true });
        success = true;
      } catch (userErr) {
        console.warn("Aviso ao salvar torneio na coleção do usuário:", userErr);
      }
    }

    // 3. Se o torneio possui um organizerUserId diferente do usuário logado:
    if (torneio.organizerUserId && torneio.organizerUserId !== userId) {
      try {
        const organizerPath = getUserCollectionPath(torneio.organizerUserId, "torneios");
        const organizerDocRef = doc(db, organizerPath, torneio.id);
        await setDoc(organizerDocRef, cleanData, { merge: true });
      } catch (orgErr) {
        // Ignora silenciosamente se o atleta não tiver permissão para escrever na pasta privada de outro usuário
      }
    }
    // 4. Notifica abas locais via BroadcastChannel
    try {
      const channel = new BroadcastChannel("rachao_realtime_sync");
      channel.postMessage({ type: "TORNEIO_UPDATED", torneio });
      channel.close();
    } catch (bcErr) {}
  } catch (error) {
    console.error("Erro geral ao salvar torneio no Firestore:", error);
  }
  return success;
}

// Escuta em tempo real um torneio público pelo ID (para atletas que abrem o link direto de inscrição)
export function subscribePublicTournament(
  tourneyId: string,
  callback: (torneio: Tournament | null) => void
) {
  if (!isFirebaseConfigured() || !tourneyId) {
    callback(null);
    return () => {};
  }
  const publicDocRef = doc(db, "public_tournaments", tourneyId);
  return onSnapshot(
    publicDocRef,
    (snap) => {
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() } as Tournament;
        if (data.status === "excluido") {
          callback(null);
        } else {
          callback(data);
        }
      } else {
        callback(null);
      }
    },
    (error) => {
      console.error("Erro ao escutar torneio público:", error);
      callback(null);
    }
  );
}

// Exclui um torneio no Firestore e no cache local canônico
export async function deleteTorneioFirestore(userId: string | null, id: string) {
  // 1. Remove do cache local imediatamente para não ressuscitar
  try {
    const localTourneys = StorageService.get<Tournament[]>(STORAGE_KEYS.RACHAO_TORNEIOS, []) || [];
    const filtered = localTourneys.filter((t) => t.id !== id);
    StorageService.set(STORAGE_KEYS.RACHAO_TORNEIOS, filtered);
  } catch (e) {}

  // 2. Notifica abas locais via BroadcastChannel
  try {
    const channel = new BroadcastChannel("rachao_realtime_sync");
    channel.postMessage({ type: "TORNEIO_DELETED", id });
    channel.close();
  } catch (bcErr) {}

  if (!isFirebaseConfigured()) return;
  try {
    if (userId) {
      const path = getUserCollectionPath(userId, "torneios");
      const docRef = doc(db, path, id);
      await updateDoc(docRef, { status: "excluido", updatedAt: Date.now() }).catch(() => {});
    }
    const publicDocRef = doc(db, "public_tournaments", id);
    await updateDoc(publicDocRef, { status: "excluido", updatedAt: Date.now() }).catch(() => {});
  } catch (error) {
    console.error("Erro ao excluir (soft delete) torneio no Firestore:", error);
  }
}


// --- INTEGRAÇÃO GLOBAL COM QUADRAS E ARENAS ---

export interface SubscribeCourtsOptions {
  userUid?: string;
  userEmail?: string;
  isAdmin?: boolean;
  isArenaOwner?: boolean;
  accountType?: string;
  role?: string;
  onError?: (error: any) => void;
  onSuccess?: (courts: CourtItem[]) => void;
}

export function subscribeCourts(
  callback: (courts: CourtItem[]) => void,
  fallbackCourts: CourtItem[] = [],
  options?: SubscribeCourtsOptions
) {
  if (!isFirebaseConfigured()) {
    const list = options?.isAdmin || options?.isArenaOwner || options?.accountType === "dono_arena" ? fallbackCourts : [];
    callback(list);
    if (typeof options?.onSuccess === "function") {
      options.onSuccess(list);
    }
    return () => {};
  }

  try {
    const currentAuthUser = auth?.currentUser;
    const localProfile: any = UserProfileRepository.getProfile<any>();

    // Validação de posse: localProfile do storage só é considerado se pertencer comprovadamente ao usuário autenticado
    const isLocalProfileMatchingAuth = Boolean(
      currentAuthUser &&
      localProfile &&
      localProfile.id !== "guest_visitor" &&
      (localProfile.id === currentAuthUser.uid || (currentAuthUser.email && localProfile.email?.toLowerCase() === currentAuthUser.email.toLowerCase()))
    );
    const safeLocalProfile = isLocalProfileMatchingAuth ? localProfile : null;

    const uid = options?.userUid || currentAuthUser?.uid || safeLocalProfile?.id;
    const email = options?.userEmail || currentAuthUser?.email || safeLocalProfile?.email;

    const checkIsAdmin = Boolean(options?.isAdmin ?? (
      (email && OFFICIAL_ADMIN_EMAILS.some((e) => e.toLowerCase() === String(email).trim().toLowerCase())) ||
      options?.role === "admin" ||
      safeLocalProfile?.accountType === "admin" ||
      safeLocalProfile?.role === "admin" ||
      safeLocalProfile?.roleMode === "admin"
    ));

    const isExplicitOwner = Boolean(
      options?.isArenaOwner === true ||
      options?.accountType === "dono_arena" ||
      options?.role === "dono_arena" ||
      safeLocalProfile?.accountType === "dono_arena" ||
      safeLocalProfile?.role === "dono_arena" ||
      safeLocalProfile?.roleMode === "dono_arena" ||
      (options?.accountType === undefined && options?.isArenaOwner === undefined && safeLocalProfile?.accountType === "dono_arena")
    );

    const isExplicitAthlete = Boolean(
      options?.accountType === "atleta" ||
      options?.role === "atleta" ||
      options?.isArenaOwner === false ||
      (!isExplicitOwner && !checkIsAdmin)
    );

    const checkIsOwner = !checkIsAdmin && (isExplicitOwner || (!isExplicitAthlete && isExplicitOwner));

    // Emite fallback transitório antes do primeiro snapshot: para donos autenticados e admins com fallback
    if (checkIsOwner || (checkIsAdmin && Array.isArray(fallbackCourts) && fallbackCourts.length > 0)) {
      callback(fallbackCourts);
    } else {
      callback([]);
    }

    if (checkIsAdmin) {
      // Administradores globais possuem permissão read total via isAdmin() nas Rules
      const q = query(collection(db, "courts"));
      return onSnapshot(
        q,
        (snapshot) => {
          const courtsList: CourtItem[] = [];
          snapshot.forEach((docSnap) => {
            const data = docSnap.data();
            const courtId = docSnap.id;
            courtsList.push({ id: courtId, ...data } as CourtItem);
          });
          try {
            localStorage.setItem("app_arena_courts_canonical_admin_v1", JSON.stringify(courtsList));
          } catch {}
          callback(courtsList);
          if (typeof options?.onSuccess === "function") {
            options.onSuccess(courtsList);
          }
        },
        (error) => {
          console.error("Erro ao escutar quadras no Firestore (Admin):", error);
          try {
            const cached = localStorage.getItem("app_arena_courts_canonical_admin_v1") || localStorage.getItem("app_arena_courts_v7");
            if (cached) {
              const parsed = JSON.parse(cached);
              if (Array.isArray(parsed) && parsed.length > 0) {
                callback(parsed);
                if (typeof options?.onError === "function") {
                  options.onError(error);
                }
                return;
              }
            }
          } catch {}
          callback([]);
          if (typeof options?.onError === "function") {
            options.onError(error);
          }
        }
      );
    } else if (checkIsOwner) {
      if (!uid || typeof uid !== "string" || uid.trim().length === 0) {
        // Dono sem UID carregado ainda: apenas emite lista vazia e aguarda
        callback([]);
        if (typeof options?.onSuccess === "function") {
          options.onSuccess([]);
        }
        return () => {};
      }
      const cleanUid = uid.trim();
      const q = query(
        collection(db, "courts"),
        or(
          where("ownerId", "==", cleanUid),
          where("ownerUid", "==", cleanUid),
          where("userId", "==", cleanUid),
          where("arenaId", "==", cleanUid)
        )
      );
      return onSnapshot(
        q,
        (snapshot) => {
          const courtsList: CourtItem[] = [];
          snapshot.forEach((docSnap) => {
            const data = docSnap.data();
            const courtId = docSnap.id;
            courtsList.push({ id: courtId, ...data } as CourtItem);
          });
          callback(courtsList);
          if (typeof options?.onSuccess === "function") {
            options.onSuccess(courtsList);
          }
        },
        (error) => {
          console.error("Erro ao escutar quadras no Firestore (Dono):", error);
          callback([]);
          if (typeof options?.onError === "function") {
            options.onError(error);
          }
        }
      );
    } else {
      // Público ou atletas não autenticados: consultas públicas canônicas 100% autorizadas e compatíveis no Firestore
      // Cobertura completa dos valores canônicos aceitos: "active", "ATIVA", "ACTIVE"
      // Cada query possui apenas 1 operador "in" para cumprir a limitação do Firestore SDK e alinhar às Rules
      const qActiveLower = query(
        collection(db, "courts"),
        where("verificationStatus", "in", ["VERIFIED", "VERIFICADA"]),
        where("isActive", "==", true),
        where("status", "==", "active")
      );

      const qAtiva = query(
        collection(db, "courts"),
        where("verificationStatus", "in", ["VERIFIED", "VERIFICADA"]),
        where("isActive", "==", true),
        where("status", "==", "ATIVA")
      );

      const qActiveUpper = query(
        collection(db, "courts"),
        where("verificationStatus", "in", ["VERIFIED", "VERIFICADA"]),
        where("isActive", "==", true),
        where("status", "==", "ACTIVE")
      );

      let mapActiveLower = new Map<string, CourtItem>();
      let mapAtiva = new Map<string, CourtItem>();
      let mapActiveUpper = new Map<string, CourtItem>();

      // Avaliação estrita de segurança conforme a regra pública definida em firestore.rules
      const isPubliclyAllowed = (court: CourtItem): boolean => {
        const isVerifiedArena = court.isVerifiedArena === true;
        const isStatusVerified = ["VERIFIED", "VERIFICADA"].includes(court.verificationStatus || "");
        const hasVerification = isVerifiedArena || isStatusVerified;

        const docStatus = court.status || "active";
        const docVerStatus = court.verificationStatus || "";

        const statusNotBlocked = docStatus !== "BLOCKED" && docStatus !== "SUSPENDED";
        const verStatusNotBlocked = docVerStatus !== "BLOCKED" && docVerStatus !== "SUSPENDED";
        const isStatusActive = ["active", "ATIVA", "ACTIVE"].includes(docStatus);

        return hasVerification && statusNotBlocked && verStatusNotBlocked && isStatusActive;
      };

      const emitPublicCourts = () => {
        const mergedMap = new Map<string, CourtItem>();
        mapActiveLower.forEach((court, id) => mergedMap.set(id, court));
        mapAtiva.forEach((court, id) => mergedMap.set(id, court));
        mapActiveUpper.forEach((court, id) => mergedMap.set(id, court));

        if (isFinancialDebugEnabled()) {
          console.log(`🔍 [PUBLIC QUERY DIAGNOSTIC] Queries merged: ${mergedMap.size} docs (active: ${mapActiveLower.size}, ATIVA: ${mapAtiva.size}, ACTIVE: ${mapActiveUpper.size})`);
          mergedMap.forEach((court, id) => {
            console.log(`  [Public Doc] courtId: ${id}, arenaId: ${court.arenaId || "N/A"}, verificationStatus: ${court.verificationStatus}, isVerifiedArena: ${court.isVerifiedArena}, isActive: ${court.isActive}, status: ${court.status}`);
          });
        }

        const publicMap = new Map<string, CourtItem>();
        mergedMap.forEach((court, id) => {
          const allowed = isPubliclyAllowed(court);
          if (isFinancialDebugEnabled()) {
            console.log(`🔍 [isPubliclyAllowed] courtId: ${id}, arenaId: ${court.arenaId || "N/A"}, allowed: ${allowed}`);
          }
          if (allowed) publicMap.set(id, court);
        });

        const publicList = Array.from(publicMap.values());
        if (isFinancialDebugEnabled()) {
          console.log(`🔍 [PUBLIC QUERY DIAGNOSTIC] Final Allowed: ${publicList.length} courts`);
        }

        callback(publicList);
        if (typeof options?.onSuccess === "function") {
          options.onSuccess(publicList);
        }
      };

      const unsub1 = onSnapshot(
        qActiveLower,
        (snapshot) => {
          mapActiveLower = new Map();
          snapshot.forEach((docSnap) => {
            mapActiveLower.set(docSnap.id, { id: docSnap.id, ...docSnap.data() } as CourtItem);
          });
          emitPublicCourts();
        },
        (error) => {
          console.error("Erro ao escutar quadras públicas (status 'active'):", error);
        }
      );

      const unsub2 = onSnapshot(
        qAtiva,
        (snapshot) => {
          mapAtiva = new Map();
          snapshot.forEach((docSnap) => {
            mapAtiva.set(docSnap.id, { id: docSnap.id, ...docSnap.data() } as CourtItem);
          });
          emitPublicCourts();
        },
        (error) => {
          console.error("Erro ao escutar quadras públicas (status 'ATIVA'):", error);
        }
      );

      const unsub3 = onSnapshot(
        qActiveUpper,
        (snapshot) => {
          mapActiveUpper = new Map();
          snapshot.forEach((docSnap) => {
            mapActiveUpper.set(docSnap.id, { id: docSnap.id, ...docSnap.data() } as CourtItem);
          });
          emitPublicCourts();
        },
        (error) => {
          console.error("Erro ao escutar quadras públicas (status 'ACTIVE'):", error);
        }
      );

      return () => {
        unsub1();
        unsub2();
        unsub3();
      };
    }
  } catch (err) {
    console.error("Erro na inscrição de quadras:", err);
    return () => {};
  }
}

export async function saveCourtFirestore(court: CourtItem, isNewCourt: boolean = false) {
  // Recupera perfil local para fallback de dados do proprietário se não informados
  let localProfile: any = UserProfileRepository.getProfile<any>();
  const currentUid = auth?.currentUser?.uid;
  const currentEmail = auth?.currentUser?.email;

  // Garante arenaId canônico (nunca id da quadra e nunca vazio se o dono tiver arena)
  const rawArenaId = court.arenaId || (court as any).arenaOwnerId || localProfile?.arenaId;
  const isInvalidArena = !rawArenaId || rawArenaId === court.id || rawArenaId.startsWith("court_") || rawArenaId.startsWith("quadra_");
  const canonicalArenaId = !isInvalidArena ? rawArenaId : (localProfile?.arenaId || (currentUid ? `arena_${currentUid}` : ""));

  if (isNewCourt) {
    // 1. Identificar o número de quadras pertencentes a esta Arena no cache local
    const cachedCourts = StorageService.get<CourtItem[]>(STORAGE_KEYS.RACHAO_COURTS, []) || [];
    const arenaCourts = cachedCourts.filter((c) => c.arenaId === canonicalArenaId);
    const currentCourtCount = arenaCourts.length;

    // 2. Resolver o plano comercial efetivo e modelo
    let planId = "";
    let model = "";

    if (isFirebaseConfigured()) {
      try {
        const configDoc = await getDoc(doc(db, "arena_financial_configs", canonicalArenaId));
        if (configDoc.exists()) {
          const data = configDoc.data();
          planId = data?.commercialPlanId || "";
          model = data?.financialModel || "";
        }
      } catch (err) {
        console.warn("Erro ao buscar arena_financial_configs no Firestore:", err);
      }
    }

    if (!planId) {
      planId = localProfile?.commercialPlanId || localProfile?.financialConfig?.commercialPlanId || "";
    }
    if (!model) {
      model = localProfile?.financialModel || localProfile?.arenaFinancialModel || localProfile?.financialConfig?.financialModel || "SPLIT";
    }

    let maxCourts: number | null = null;
    let finalModel = model;

    if (isFirebaseConfigured() && planId) {
      try {
        const planDoc = await getDoc(doc(db, "commercial_plans", planId));
        if (planDoc.exists()) {
          const data = planDoc.data();
          maxCourts = data?.maxCourts !== undefined ? data?.maxCourts : null;
          finalModel = data?.financialModel || finalModel;
        }
      } catch (err) {
        console.warn("Erro ao buscar plano no Firestore:", err);
      }
    }

    const DEFAULT_PLAN_LIMITS: Record<string, { model: string, maxCourts: number | null }> = {
      fixed_1_2: { model: "FIXED_MONTHLY", maxCourts: 2 },
      fixed_3_4: { model: "FIXED_MONTHLY", maxCourts: 4 },
      fixed_5_6: { model: "FIXED_MONTHLY", maxCourts: 6 },
      fixed_7_10: { model: "FIXED_MONTHLY", maxCourts: 10 },
      fixed_11_15: { model: "FIXED_MONTHLY", maxCourts: 15 },
      fixed_16_open: { model: "FIXED_MONTHLY", maxCourts: null },
      hybrid_1_3: { model: "HYBRID", maxCourts: 3 },
      hybrid_4_6: { model: "HYBRID", maxCourts: 6 },
      hybrid_7_10: { model: "HYBRID", maxCourts: 10 },
      hybrid_11_15: { model: "HYBRID", maxCourts: 15 },
      hybrid_16_open: { model: "HYBRID", maxCourts: null },
      split_1_open: { model: "SPLIT", maxCourts: null },
      plan_pdv_vendas_locais: { model: "PDV_VENDAS_LOCAIS", maxCourts: null },
      plan_operation_only: { model: "OPERATION_ONLY", maxCourts: null },
    };

    if (maxCourts === null && planId && DEFAULT_PLAN_LIMITS[planId]) {
      maxCourts = DEFAULT_PLAN_LIMITS[planId].maxCourts;
      finalModel = DEFAULT_PLAN_LIMITS[planId].model;
    }

    // Regra: se o modelo for SPLIT, ou maxCourts for null/<=0, o limite é ilimitado
    const isUnlimited = finalModel === "SPLIT" || maxCourts === null || maxCourts <= 0;
    if (!isUnlimited && maxCourts !== null && currentCourtCount >= maxCourts) {
      const errorMsg = `Seu plano atual permite até ${maxCourts} quadras. Para criar mais uma quadra, altere seu plano comercial.`;
      throw new Error(errorMsg);
    }

    // Regra canônica: Toda nova quadra criada pelo proprietário nasce obrigatoriamente como:
    // status = "PENDING", verificationStatus = "PENDING_APPROVAL", isVerifiedArena = false
    court.status = "PENDING";
    court.verificationStatus = "PENDING_APPROVAL";
    court.isVerifiedArena = false;
  }

  const enrichedCourt: CourtItem = {
    ...court,
    arenaId: canonicalArenaId || court.arenaId,
    ownerId: court.ownerId || currentUid || localProfile?.id || "",
    ownerName: (court.ownerName && court.ownerName !== "Proprietário" && !court.ownerName.startsWith("Proprietário ("))
      ? court.ownerName
      : (localProfile?.name || localProfile?.arenaName || court.ownerName || "Proprietário"),
    ownerEmail: (court.ownerEmail && court.ownerEmail !== "contato@arena.com" && court.ownerEmail !== "email@naoinformado.com")
      ? court.ownerEmail
      : (currentEmail || localProfile?.email || court.ownerEmail || ""),
    ownerPhone: (court.ownerPhone && court.ownerPhone !== "(00) 00000-0000" && court.ownerPhone !== "6")
      ? court.ownerPhone
      : (localProfile?.phone || localProfile?.whatsappContact || court.ownerPhone || ""),
    ownerCpfCnpj: (court.ownerCpfCnpj && court.ownerCpfCnpj !== "00.000.000/0001-00" && court.ownerCpfCnpj !== "Não informado")
      ? court.ownerCpfCnpj
      : (localProfile?.cnpj || localProfile?.cpf || localProfile?.arenaVerification?.cnpj || court.ownerCpfCnpj || ""),
    arenaImageUrl: (court.arenaImageUrl && court.arenaImageUrl.trim().length > 0)
      ? court.arenaImageUrl
      : (localProfile?.arenaImageUrl || undefined),
    arenaLogoUrl: (court.arenaLogoUrl && court.arenaLogoUrl.trim().length > 0)
      ? court.arenaLogoUrl
      : (localProfile?.arenaLogoUrl || localProfile?.avatarUrl || undefined),
  };

  // Validação da comissão customizada da quadra
  if (court.customCommissionRate !== undefined && court.customCommissionRate !== null) {
    const valRes = validateCustomCommissionRate(court.customCommissionRate);
    if (!valRes.valid) {
      console.error("Tentativa de salvar customCommissionRate inválida:", valRes.error);
      throw new Error(valRes.error);
    }
    enrichedCourt.customCommissionRate = valRes.normalizedRate;
  }

  // Recupera a quadra existente para verificar se ela já tinha uma foto
  let existingPhotoUrl: string | undefined = undefined;
  try {
    const cached = StorageService.get<CourtItem[]>(STORAGE_KEYS.RACHAO_COURTS, []) || [];
    const matched = cached.find((c) => c.id === enrichedCourt.id);
    if (matched && matched.courtPhotoUrl) {
      existingPhotoUrl = matched.courtPhotoUrl;
    }
  } catch {}

  if (enrichedCourt.courtPhotoUrl === "REMOVE" || enrichedCourt.courtPhotoUrl === "CLEAR") {
    enrichedCourt.courtPhotoUrl = "";
  } else if (!enrichedCourt.courtPhotoUrl || enrichedCourt.courtPhotoUrl === "" || enrichedCourt.courtPhotoUrl === "null" || enrichedCourt.courtPhotoUrl === "undefined") {
    if (existingPhotoUrl) {
      enrichedCourt.courtPhotoUrl = existingPhotoUrl;
    }
  }

  if (isNewCourt) {
    enrichedCourt.status = "PENDING";
    enrichedCourt.verificationStatus = "PENDING_APPROVAL";
    enrichedCourt.isVerifiedArena = false;
    (enrichedCourt as any).isActive = false;
  }

  // Sincroniza sempre o cache local com StorageService (offline/test-safe)
  try {
    const cached = StorageService.get<CourtItem[]>(STORAGE_KEYS.RACHAO_COURTS, []) || [];
    const idx = cached.findIndex((c) => c.id === enrichedCourt.id);
    if (idx >= 0) {
      cached[idx] = enrichedCourt;
    } else {
      cached.push(enrichedCourt);
    }
    StorageService.set(STORAGE_KEYS.RACHAO_COURTS, cached);
  } catch {}

  if (!isFirebaseConfigured()) return;
  try {
    const docRef = doc(db, "courts", enrichedCourt.id);
    const batch = writeBatch(db);
    batch.set(docRef, sanitizeCourtForFirestore(enrichedCourt), { merge: true });

    // Somente quadras ATIVAS/VERIFICADAS contam para activeCourtCount.
    // Quadras PENDING, UNDER_REVIEW, BLOCKED, SUSPENDED não contam.
    // Como novas quadras nascem PENDING, o incremento prematuro na criação é evitado.
    const isCourtActive = (enrichedCourt.status === "active" || (enrichedCourt as any).status === "ACTIVE") &&
      (enrichedCourt.verificationStatus === "VERIFIED" || enrichedCourt.isVerifiedArena === true);

    if (isNewCourt && isCourtActive && enrichedCourt.arenaId) {
      const configRef = doc(db, "arena_financial_configs", enrichedCourt.arenaId);
      batch.set(configRef, { activeCourtCount: increment(1), lastModifiedCourtId: enrichedCourt.id }, { merge: true });
    }

    await batch.commit();
  } catch (error) {
    console.error("Erro ao salvar quadra no Firestore:", error);
  }
}

/**
 * Atualiza campos específicos de uma quadra no Firestore usando updateDoc.
 * Impede que arrays não relacionados (como slots) sejam reenviados e sobrescrevam dados concorrentes.
 */
export async function updateCourtPartialFirestore(courtId: string, fields: Record<string, any>): Promise<void> {
  try {
    const cached = StorageService.get<CourtItem[]>(STORAGE_KEYS.RACHAO_COURTS, []) || [];
    const idx = cached.findIndex((c) => c.id === courtId);
    if (idx >= 0) {
      cached[idx] = { ...cached[idx], ...fields };
      StorageService.set(STORAGE_KEYS.RACHAO_COURTS, cached);
    }
  } catch {}

  try {
    const saved = localStorage.getItem("app_arena_courts_v6");
    if (saved) {
      const list = JSON.parse(saved) || [];
      const idx = list.findIndex((c: any) => c.id === courtId);
      if (idx >= 0) {
        list[idx] = { ...list[idx], ...fields };
        localStorage.setItem("app_arena_courts_v6", JSON.stringify(list));
      }
    }
  } catch {}

  if (!isFirebaseConfigured() || !courtId || !db) return;
  try {
    const docRef = doc(db, "courts", courtId);
    let existingPhotoUrl: string | undefined = undefined;
    try {
      const cached = StorageService.get<CourtItem[]>(STORAGE_KEYS.RACHAO_COURTS, []) || [];
      const matched = cached.find((c) => c.id === courtId);
      if (matched && matched.courtPhotoUrl) {
        existingPhotoUrl = matched.courtPhotoUrl;
      }
    } catch {}

    const sanitized = sanitizeCourtForFirestore(fields);

    if (sanitized.courtPhotoUrl === "REMOVE" || sanitized.courtPhotoUrl === "CLEAR") {
      sanitized.courtPhotoUrl = "";
    } else if (!sanitized.courtPhotoUrl || sanitized.courtPhotoUrl === "" || sanitized.courtPhotoUrl === "null" || sanitized.courtPhotoUrl === "undefined") {
      if (existingPhotoUrl) {
        sanitized.courtPhotoUrl = existingPhotoUrl;
      }
    }

    // Se houver customCommissionRate, valida antes
    if (sanitized.customCommissionRate !== undefined && sanitized.customCommissionRate !== null) {
      const valRes = validateCustomCommissionRate(sanitized.customCommissionRate);
      if (!valRes.valid) {
        throw new Error(valRes.error);
      }
      sanitized.customCommissionRate = valRes.normalizedRate;
    }
    await setDoc(docRef, sanitized, { merge: true });
  } catch (error) {
    console.error(`Erro ao atualizar campos parciais da quadra ${courtId} no Firestore:`, error);
    throw error;
  }
}

export async function deleteCourtFirestore(courtId: string, courtBefore?: any) {
  if (!isFirebaseConfigured()) {
    try {
      const cached = StorageService.get<CourtItem[]>(STORAGE_KEYS.RACHAO_COURTS, []) || [];
      const filtered = cached.filter((c) => c.id !== courtId);
      StorageService.set(STORAGE_KEYS.RACHAO_COURTS, filtered);
      if (typeof localStorage !== "undefined") {
        const raw = localStorage.getItem("app_arena_courts_v7");
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            const f = parsed.filter((c: any) => c && c.id !== courtId);
            if (f.length === 0) {
              localStorage.removeItem("app_arena_courts_v7");
            } else {
              localStorage.setItem("app_arena_courts_v7", JSON.stringify(f));
            }
          }
        }
      }
    } catch {}
    return;
  }

  try {
    const docRef = doc(db, "courts", courtId);
    let finalCourtBefore = courtBefore;
    if (!finalCourtBefore) {
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        finalCourtBefore = snap.data();
      }
    }

    const arenaId = finalCourtBefore?.arenaId;
    const isStatusActive = finalCourtBefore && arenaId &&
      (finalCourtBefore.status === "active" || finalCourtBefore.status === "ACTIVE");

    if (isStatusActive) {
      const batch = writeBatch(db);
      batch.delete(docRef);
      const configRef = doc(db, "arena_financial_configs", arenaId);
      batch.set(
        configRef,
        {
          arenaId: arenaId,
          activeCourtCount: increment(-1),
          lastModifiedCourtId: courtId,
        },
        { merge: true }
      );
      await batch.commit();

      // Recalcula deterministicamente para atualizar cache local sem segunda escrita redundante no Firestore
      await recalculateArenaActiveCourtsCount(arenaId, undefined, courtId, {
        preventFirestoreWrite: true,
      });
    } else {
      await deleteDoc(docRef);
      if (arenaId) {
        await recalculateArenaActiveCourtsCount(arenaId, undefined, courtId);
      }
    }

    // Só atualiza o storage local se a exclusão foi confirmada no Firestore
    try {
      const cached = StorageService.get<CourtItem[]>(STORAGE_KEYS.RACHAO_COURTS, []) || [];
      const filtered = cached.filter((c) => c.id !== courtId);
      StorageService.set(STORAGE_KEYS.RACHAO_COURTS, filtered);
    } catch {}

    try {
      if (typeof localStorage !== "undefined") {
        const raw = localStorage.getItem("app_arena_courts_v7");
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            const filtered = parsed.filter((c: any) => c && c.id !== courtId);
            if (filtered.length === 0) {
              localStorage.removeItem("app_arena_courts_v7");
            } else {
              localStorage.setItem("app_arena_courts_v7", JSON.stringify(filtered));
            }
          }
        }
      }
    } catch {}

  } catch (error) {
    console.error("Erro ao deletar quadra no Firestore:", error);
    throw error;
  }
}

/**
 * Recalcula de forma determinística e atômica o activeCourtCount de uma Arena,
 * garantindo que apenas quadras pertencentes à arena, com aprovação formal
 * (isCourtApproved) e operacionalmente ativas (isCourtOperationallyActive) sejam contabilizadas.
 * Garante activeCourtCount >= 0 e nunca altera o financialModel da Arena.
 */
export async function recalculateArenaActiveCourtsCount(
  arenaId: string,
  courtsList?: CourtItem[],
  lastModifiedCourtId?: string,
  options?: {
    batch?: any;
    preventFirestoreWrite?: boolean;
  }
): Promise<number> {
  if (!arenaId) return 0;

  let targetCourts: CourtItem[] = [];
  if (courtsList && Array.isArray(courtsList)) {
    targetCourts = courtsList.filter((c) => c && c.arenaId === arenaId);
  } else {
    try {
      if (isFirebaseConfigured() && db) {
        const q = query(collection(db, "courts"), where("arenaId", "==", arenaId));
        const snap = await getDocs(q);
        targetCourts = snap.docs.map((d) => ({ id: d.id, ...d.data() } as unknown as CourtItem));
      } else {
        const cached = StorageService.get<CourtItem[]>(STORAGE_KEYS.RACHAO_COURTS, []) || [];
        targetCourts = cached.filter((c: any) => c && c.arenaId === arenaId);
      }
    } catch {
      targetCourts = [];
    }
  }

  const activeCount = targetCourts.filter((c) => {
    const vStatus = String(c.verificationStatus || "").toUpperCase();
    const docStatus = String(c.status || "").toLowerCase();

    const isApproved =
      vStatus === "VERIFIED" ||
      vStatus === "APPROVED" ||
      vStatus === "VERIFICADA" ||
      c.isVerifiedArena === true ||
      ((docStatus === "active" || docStatus === "ativa") && c.isVerifiedArena !== false && vStatus !== "PENDING_APPROVAL");

    const isNonPendingOrRejected =
      vStatus !== "PENDING_APPROVAL" &&
      vStatus !== "PENDING" &&
      vStatus !== "UNDER_REVIEW" &&
      vStatus !== "REJECTED" &&
      docStatus !== "pending" &&
      docStatus !== "rejected";

    const isOpActive = (c as any).isActive !== false && docStatus !== "inactive" && docStatus !== "inativa";

    return isApproved && isNonPendingOrRejected && isOpActive;
  }).length;

  const validCount = Math.max(0, activeCount);

  if (isFirebaseConfigured() && db && !options?.preventFirestoreWrite) {
    try {
      const configRef = doc(db, "arena_financial_configs", arenaId);
      const updateData: any = { activeCourtCount: validCount, updatedAt: Date.now() };
      if (lastModifiedCourtId) {
        updateData.lastModifiedCourtId = lastModifiedCourtId;
      }
      if (options?.batch) {
        options.batch.set(configRef, updateData, { merge: true });
      } else {
        await setDoc(configRef, updateData, { merge: true });
      }
    } catch (err) {
      console.warn(`[recalculateArenaActiveCourtsCount] Aviso ao atualizar Firestore para ${arenaId}:`, err);
    }
  }

  try {
    const storedConfigs = StorageService.get<Record<string, any>>("app_arena_financial_configs", {}) || {};
    const currentConfig = storedConfigs[arenaId] || {};
    storedConfigs[arenaId] = {
      ...currentConfig,
      activeCourtCount: validCount,
      updatedAt: Date.now(),
    };
    StorageService.set("app_arena_financial_configs", storedConfigs);
  } catch {}

  return validCount;
}

// --- INTEGRAÇÃO COM PERFIL DO USUÁRIO NO FIRESTORE ---

export async function ensureCanonicalArenaFirestore(options: {
  ownerUid: string;
  arenaId?: string;
  arenaName?: string;
  city?: string;
  state?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  zipCode?: string;
  address?: string;
  phone?: string;
  email?: string;
  cpfCnpj?: string;
  status?: string;
  arenaImageUrl?: string;
  arenaLogoUrl?: string;
}): Promise<string> {
  const { ownerUid, arenaName, city, state, phone, email, cpfCnpj, status, arenaImageUrl, arenaLogoUrl } = options;
  if (!ownerUid) return "";

  // Determinístico e canônico:
  // - arenaId NUNCA é igual a ownerUid
  // - arenaId NUNCA é ID de quadra (ex: court_..., quadra_...)
  // - arenaId NUNCA é fallback genérico ("arena_padrao")
  const isInvalidArenaId =
    !options.arenaId ||
    options.arenaId === ownerUid ||
    options.arenaId === "arena_padrao" ||
    options.arenaId.startsWith("court_") ||
    options.arenaId.startsWith("quadra_");

  const canonicalArenaId = !isInvalidArenaId
    ? options.arenaId!
    : (ownerUid.startsWith("arena_") ? ownerUid : `arena_${ownerUid}`);

  const now = Date.now();
  const arenaDocData: Partial<ArenaFirestoreDoc> = {
    id: canonicalArenaId,
    arenaId: canonicalArenaId,
    canonicalArenaId: canonicalArenaId,
    ownerUid: ownerUid,
    ownerId: ownerUid,
    arenaName: arenaName || "Arena Esportiva",
    name: arenaName || "Arena Esportiva",
    city: city || "Não informada",
    state: state || "Não informado",
    phone: phone || "",
    whatsapp: phone || "",
    ownerEmail: email || "",
    cnpj: cpfCnpj || "",
    chargeBookings: true,
    updatedAt: now,
  };

  if (arenaImageUrl) arenaDocData.arenaImageUrl = arenaImageUrl;
  if (arenaLogoUrl) arenaDocData.arenaLogoUrl = arenaLogoUrl;
  if (options.street !== undefined) arenaDocData.street = options.street;
  if (options.number !== undefined) arenaDocData.number = options.number;
  if (options.complement !== undefined) arenaDocData.complement = options.complement;
  if (options.neighborhood !== undefined) arenaDocData.neighborhood = options.neighborhood;
  if (options.zipCode !== undefined) arenaDocData.zipCode = options.zipCode;
  if (options.address !== undefined) {
    arenaDocData.address = options.address;
  } else if (options.street) {
    arenaDocData.address = `${options.street}${options.number ? `, ${options.number}` : ""}${options.neighborhood ? ` - ${options.neighborhood}` : ""}`;
  }

  let docExists = false;
  if (isFirebaseConfigured() && db && auth?.currentUser) {
    try {
      const docRef = doc(db, "arenas", canonicalArenaId);
      const docSnap = await getDoc(docRef);
      docExists = docSnap.exists();
    } catch {}
  }

  // Define campos operacionais somente se status for fornecido ou se for um documento novo
  if (status !== undefined && status !== null) {
    arenaDocData.status = status;
    arenaDocData.verificationStatus = status === "ATIVA" ? "VERIFIED" : "PENDING_APPROVAL";
    arenaDocData.isActive = status === "ATIVA";
  } else if (!docExists) {
    // Nova arena criada pela primeira vez nasce em análise
    arenaDocData.status = "EM_ANALISE";
    arenaDocData.verificationStatus = "PENDING_APPROVAL";
    arenaDocData.isActive = false;
    arenaDocData.createdAt = now;
  }

  // 1. Atualiza cache local
  try {
    const saved = localStorage.getItem("app_arenas_canonical_v1");
    let list: any[] = [];
    if (saved) {
      try {
        list = JSON.parse(saved) || [];
      } catch {}
    }
    const idx = list.findIndex((a) => a.id === canonicalArenaId);
    if (idx >= 0) {
      list[idx] = { ...list[idx], ...sanitizeForFirestore(arenaDocData), updatedAt: now };
    } else {
      list.push({ ...sanitizeForFirestore(arenaDocData) });
    }
    localStorage.setItem("app_arenas_canonical_v1", JSON.stringify(list));
  } catch {}

  // 2. Persiste na coleção canônica /arenas
  if (isFirebaseConfigured() && db && auth?.currentUser) {
    try {
      const docRef = doc(db, "arenas", canonicalArenaId);
      await setDoc(docRef, sanitizeForFirestore(arenaDocData), { merge: true });

      // Se a arena for recém-criada, inicializa também a configuração comercial canônica padrão (SPLIT)
      if (!docExists) {
        try {
          const pubRef = doc(db, "arena_public_configs", canonicalArenaId);
          const privRef = doc(db, "arena_financial_configs", canonicalArenaId);
          const pubSnap = await getDoc(pubRef);
          if (!pubSnap.exists()) {
            const syncTimestamp = new Date().toISOString();
            const batch = writeBatch(db);
            batch.set(pubRef, {
              arenaId: canonicalArenaId,
              financialModel: "SPLIT",
              useCustomFinancialConfig: false,
              depositConfig: { required: true, type: "PERCENTAGE", percentage: 50, fixedAmount: 0 },
              updatedAt: syncTimestamp,
              ruleVersion: "3.0.0",
            }, { merge: true });
            batch.set(privRef, {
              arenaId: canonicalArenaId,
              arenaName: arenaName || "Arena Esportiva",
              financialModel: "SPLIT",
              useCustomFinancialConfig: false,
              commissionRate: 10,
              monthlyFee: 0,
              depositConfig: { required: true, type: "PERCENTAGE", percentage: 50, fixedAmount: 0 },
              updatedAt: syncTimestamp,
              ruleVersion: "3.0.0",
            }, { merge: true });
            await batch.commit();
          }
        } catch (initErr) {
          console.warn("Aviso ao auto-inicializar arena_public_configs / arena_financial_configs:", initErr);
        }
      }
    } catch (err) {
      console.warn("Aviso ao sincronizar arena canônica no Firestore:", err);
    }
  }

  return canonicalArenaId;
}

export async function saveUserProfileFirestore(profile: UserProfile) {
  if (!isFirebaseConfigured() || !auth?.currentUser?.uid) return;
  try {
    const currentUid = auth.currentUser.uid;
    const currentEmail = auth.currentUser.email;

    // Garante que o ID do perfil seja prioritariamente o UID do usuário autenticado no Firebase
    const targetId = currentUid;
    if (!targetId) return;

    // Se for proprietário de arena, garante que exista documento na coleção /arenas
    const pAny = profile as any;
    const isOwner = Boolean(
      profile.accountType === "dono_arena" ||
      pAny.accountType === "arena_owner" ||
      profile.role === "dono_arena" ||
      pAny.role === "court_owner" ||
      pAny.roleMode === "dono_arena" ||
      pAny.isArenaOwner === true
    );

    let resolvedArenaId = profile.arenaId;
    const hasExplicitArena = Boolean(
      profile.arenaId &&
      profile.arenaId.trim().length > 0 &&
      profile.arenaId !== "arena_padrao" &&
      profile.arenaId !== "arena_default"
    );
    if (isOwner && hasExplicitArena) {
      resolvedArenaId = await ensureCanonicalArenaFirestore({
        ownerUid: targetId,
        arenaId: profile.arenaId,
        arenaName: profile.arenaName || profile.name,
        city: profile.city,
        state: profile.state,
        phone: profile.phone || profile.whatsappContact,
        email: profile.email || currentEmail || "",
        cpfCnpj: profile.cnpj || profile.cpf,
        arenaImageUrl: profile.arenaImageUrl,
        arenaLogoUrl: profile.arenaLogoUrl,
      });
    }

    const finalProfileToSave: UserProfile = {
      ...profile,
      id: targetId,
      arenaId: resolvedArenaId || profile.arenaId,
      email: profile.email || currentEmail || "",
      updatedAt: Date.now(),
    };

    const sanitized = sanitizeForFirestore(finalProfileToSave);

    // 1. Grava na coleção principal: users/{currentUser.uid}
    const docRef = doc(db, "users", targetId);
    await setDoc(docRef, sanitized, { merge: true });

    // 2. Grava também na coleção auxiliar de e-mails para consultas e busca rápida pelo e-mail
    if (finalProfileToSave.email) {
      const emailClean = finalProfileToSave.email.toLowerCase().trim().replace(/[^a-zA-Z0-9_@.-]/g, "_");
      const emailDocRef = doc(db, "users_by_email", emailClean);
      await setDoc(emailDocRef, sanitized, { merge: true });
    }
    console.log("✅ Perfil sincronizado com sucesso no Firestore:", targetId, finalProfileToSave.email);
  } catch (error) {
    console.error("Erro ao salvar perfil no Firestore:", error);
  }
}

export async function loadUserProfileFirestore(userKey: string): Promise<UserProfile | null> {
  if (!isFirebaseConfigured() || !userKey || !auth?.currentUser) return null;
  try {
    // 1. Tenta buscar diretamente pelo UID em users/{userKey}
    const docRef = doc(db, "users", userKey);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      return snap.data() as UserProfile;
    }

    // 2. Tenta buscar pela chave de e-mail em users_by_email
    if (userKey.includes("@")) {
      const emailClean = userKey.toLowerCase().trim().replace(/[^a-zA-Z0-9_@.-]/g, "_");
      const emailDocRef = doc(db, "users_by_email", emailClean);
      const emailSnap = await getDoc(emailDocRef);
      if (emailSnap.exists()) {
        return emailSnap.data() as UserProfile;
      }
    }

    // 3. Fallback: Consulta no users onde o campo email é igual a userKey
    if (userKey.includes("@")) {
      const q = query(collection(db, "users"), where("email", "==", userKey.toLowerCase().trim()));
      const querySnap = await getDocs(q);
      if (!querySnap.empty) {
        return querySnap.docs[0].data() as UserProfile;
      }
    }
  } catch (error) {
    console.error("Erro ao carregar perfil do Firestore:", error);
  }
  return null;
}

export interface ArenaFirestoreDoc {
  id: string;
  name?: string;
  arenaName?: string;
  legalName?: string;
  ownerId?: string;
  ownerUid?: string;
  userId?: string;
  arenaOwnerId?: string;
  ownerName?: string;
  ownerEmail?: string;
  contactEmail?: string;
  phone?: string;
  whatsapp?: string;
  whatsappContact?: string;
  city?: string;
  state?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  zipCode?: string;
  address?: string;
  cnpj?: string;
  cpf?: string;
  cpfCnpj?: string;
  status?: string;
  operationalStatus?: string;
  financialModel?: string;
  financialConfig?: any;
  arenaLogoUrl?: string;
  arenaImageUrl?: string;
  logoUrl?: string;
  chargeBookings?: boolean;
  paymentStatus?: "em_dia" | "inadimplente" | "pendente_5dias" | "teste_gratis";
  trialStartAt?: number;
  trialEndAt?: number;
  trialStatus?: "TRIAL_ACTIVE" | "TRIAL_EXPIRED" | "OPERATION_ACTIVE";
  commercialStatus?: "TRIAL_ACTIVE" | "TRIAL_EXPIRED" | "OPERATION_ACTIVE";
  isOperationOnly?: boolean;
  createdAt?: number | string;
  updatedAt?: number | string;
  [key: string]: any;
}

export function subscribeArenas(
  callback: (arenas: ArenaFirestoreDoc[]) => void,
  fallbackArenas: ArenaFirestoreDoc[] = []
): () => void {
  let initialList: ArenaFirestoreDoc[] = fallbackArenas;
  try {
    const saved = localStorage.getItem("app_arenas_canonical_v1");
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) {
        initialList = parsed;
      }
    }
  } catch {}

  callback(initialList);

  if (!isFirebaseConfigured() || !auth?.currentUser) {
    return () => {};
  }

  try {
    const q = query(collection(db, "arenas"));
    return onSnapshot(
      q,
      (snapshot) => {
        const arenas: ArenaFirestoreDoc[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          arenas.push({ id: docSnap.id, ...data } as ArenaFirestoreDoc);
        });
        try {
          localStorage.setItem("app_arenas_canonical_v1", JSON.stringify(arenas));
        } catch {}
        callback(arenas);
      },
      (error) => {
        console.error("Erro ao escutar coleção arenas no Firestore:", error);
      }
    );
  } catch (err) {
    console.error("Falha ao registrar listener de arenas:", err);
    return () => {};
  }
}

export async function loadAllArenasFirestore(): Promise<ArenaFirestoreDoc[]> {
  if (!isFirebaseConfigured() || !auth?.currentUser) {
    try {
      const saved = localStorage.getItem("app_arenas_canonical_v1");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch {}
    return [];
  }
  try {
    const q = query(collection(db, "arenas"));
    const querySnap = await getDocs(q);
    const arenas: ArenaFirestoreDoc[] = [];
    querySnap.forEach((docSnap) => {
      const data = docSnap.data();
      if (data) {
        arenas.push({ id: docSnap.id, ...data } as ArenaFirestoreDoc);
      }
    });
    try {
      localStorage.setItem("app_arenas_canonical_v1", JSON.stringify(arenas));
    } catch {}
    return arenas;
  } catch (error) {
    console.error("Erro ao carregar arenas do Firestore:", error);
    return [];
  }
}

export async function updateArenaFirestore(
  arenaId: string,
  data: Record<string, any>
): Promise<void> {
  if (!arenaId) return;
  const sanitized = sanitizeForFirestore(data);

  // Atualiza cache local
  try {
    const saved = localStorage.getItem("app_arenas_canonical_v1");
    let list: any[] = [];
    if (saved) {
      try {
        list = JSON.parse(saved) || [];
      } catch {}
    }
    const idx = list.findIndex((a) => a.id === arenaId);
    if (idx >= 0) {
      list[idx] = { ...list[idx], ...sanitized, updatedAt: Date.now() };
    } else {
      list.push({ id: arenaId, ...sanitized, updatedAt: Date.now() });
    }
    localStorage.setItem("app_arenas_canonical_v1", JSON.stringify(list));
  } catch {}

  if (!isFirebaseConfigured() || !db) return;
  try {
    const docRef = doc(db, "arenas", arenaId);
    await setDoc(docRef, { ...sanitized, updatedAt: Date.now() }, { merge: true });
  } catch (error) {
    console.error(`Erro ao atualizar arena ${arenaId} no Firestore:`, error);
    throw error;
  }
}

export async function loadAllUsersFirestore(): Promise<UserProfile[]> {
  if (!isFirebaseConfigured() || !auth?.currentUser) return [];
  try {
    const q = query(collection(db, "users"));
    const querySnap = await getDocs(q);
    const users: UserProfile[] = [];
    querySnap.forEach((docSnap) => {
      const data = docSnap.data() as UserProfile;
      if (data) users.push({ id: docSnap.id, ...data });
    });
    return users;
  } catch (error) {
    console.error("Erro ao carregar todos os usuários do Firestore:", error);
    return [];
  }
}

// --- INTEGRAÇÃO DE CONFIGURAÇÕES GLOBAIS DO SISTEMA (SYSTEM_SETTINGS) NO FIRESTORE ---

export async function saveSystemSettingsFirestore(settings: any) {
  if (!isFirebaseConfigured() || !auth?.currentUser) return;
  try {
    const docRef = doc(db, "system_settings", "global");
    const sanitized = sanitizeForFirestore(settings);
    // Expurgar qualquer campo de segredos de API antes de enviar ao Firestore
    if (sanitized && typeof sanitized === "object") {
      delete sanitized.asaasKey;
      delete sanitized.asaasSandboxApiKey;
      delete sanitized.asaasProductionApiKey;
      delete sanitized.asaasSandboxWebhookToken;
      delete sanitized.asaasProductionWebhookToken;
      delete sanitized.asaasWebhookToken;
    }
    await setDoc(docRef, sanitized, { merge: true });
    console.log("☁️ Configurações globais salvas e sincronizadas com sucesso no Firestore (system_settings/global).");
  } catch (error: any) {
    if (error?.code === "permission-denied" || error?.message?.includes("insufficient permissions")) {
      // Não-admin ignorado silenciosamente
      return;
    }
    console.error("Erro ao salvar system_settings no Firestore:", error);
  }
}

export async function loadSystemSettingsFirestore(): Promise<any | null> {
  if (!isFirebaseConfigured()) return null;
  try {
    const docRef = doc(db, "system_settings", "global");
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      return snap.data();
    }
  } catch (error: any) {
    if (error?.code === "permission-denied" || error?.message?.includes("insufficient permissions")) {
      return null;
    }
    console.error("Erro ao carregar system_settings do Firestore:", error);
  }
  return null;
}

export function subscribeSystemSettingsFirestore(callback: (settings: any) => void) {
  if (!isFirebaseConfigured()) return () => {};
  try {
    const docRef = doc(db, "system_settings", "global");
    return onSnapshot(
      docRef,
      (snap) => {
        if (snap.exists()) {
          callback(snap.data());
        } else {
          callback(null);
        }
      },
      (err) => {
        if (err?.code === "permission-denied" || err?.message?.includes("insufficient permissions")) {
          // Documento administrativo protegido (menor privilégio): não-admins usam configurações locais ou públicas sem erro no console
          return;
        }
        console.error("Erro na escuta de tempo real do system_settings:", err);
      }
    );
  } catch (err: any) {
    if (err?.code === "permission-denied" || err?.message?.includes("insufficient permissions")) {
      return () => {};
    }
    console.error("Erro ao registrar observador de system_settings:", err);
    return () => {};
  }
}

// Pool de promessas in-flight para evitar consultas simultâneas idênticas ao Firestore
const inFlightReconciliations = new Map<string, Promise<any[]>>();

export function reconcileFirestoreBookingsClient(currentUid: string, courts: CourtItem[] = []): Promise<any[]> {
  if (!currentUid || currentUid.startsWith("guest")) return Promise.resolve([]);

  // Se já houver uma reconciliação em andamento para o mesmo UID e conjunto de quadras, reutiliza a Promise in-flight
  const inFlightKey = `${currentUid}_${courts.map((c) => c?.id).filter(Boolean).sort().join(",")}`;
  const existingPromise = inFlightReconciliations.get(inFlightKey);
  if (existingPromise) {
    return existingPromise;
  }

  const reconciliationPromise = (async () => {
    if (!isFirebaseConfigured()) return [];
    try {
      const allDocsMap = new Map<string, any>();

      // 1. Atleta
      const athleteBookingsQuery = query(collection(db, "bookings"), where("athleteId", "==", currentUid));
      const athleteSnap = await getDocs(athleteBookingsQuery).catch(() => null);
      if (athleteSnap) {
        athleteSnap.forEach((docSnap) => {
          const data = docSnap.data();
          if (isRealCourtBookingDocument(data, docSnap.id)) {
            allDocsMap.set(docSnap.id, { id: docSnap.id, ...data });
          }
        });
      }

      // 2. Chaves de proprietário/arena agrupadas em chunks
      const ownerKeys = new Set<string>();
      ownerKeys.add(currentUid);
      const authUid = auth?.currentUser?.uid;
      if (authUid) ownerKeys.add(authUid);

      for (const court of courts) {
        if (
          court.ownerId === currentUid ||
          (court as any).arenaOwnerId === currentUid ||
          (court as any).ownerUid === currentUid ||
          (court as any).arenaId === currentUid ||
          court.userId === currentUid ||
          (court.id && court.id === `virtual_arena_${currentUid}`)
        ) {
          if (court.ownerId) ownerKeys.add(court.ownerId);
          if ((court as any).arenaOwnerId) ownerKeys.add((court as any).arenaOwnerId);
          if ((court as any).ownerUid) ownerKeys.add((court as any).ownerUid);
          if ((court as any).arenaId) ownerKeys.add((court as any).arenaId);
          if (court.userId) ownerKeys.add(court.userId);
          if (court.id && court.id.startsWith("virtual_arena_")) {
            ownerKeys.add(court.id.replace("virtual_arena_", ""));
          }
        }
      }

      const ownerKeysList = Array.from(ownerKeys).filter(Boolean);
      for (let i = 0; i < ownerKeysList.length; i += 10) {
        const chunk = ownerKeysList.slice(i, i + 10);
        const q1 = query(collection(db, "bookings"), where("ownerId", "in", chunk));
        const q2 = query(collection(db, "bookings"), where("arenaId", "in", chunk));

        const [s1, s2] = await Promise.all([
          getDocs(q1).catch(() => null),
          getDocs(q2).catch(() => null),
        ]);

        [s1, s2].forEach((snap) => {
          snap?.forEach((docSnap) => {
            const data = docSnap.data();
            if (isRealCourtBookingDocument(data, docSnap.id)) {
              allDocsMap.set(docSnap.id, { id: docSnap.id, ...data });
            }
          });
        });
      }

      // 3. Consultas complementares por courtId agrupadas em chunks com operador "in"
      const courtIds = new Set<string>();
      for (const court of courts) {
        if (!court?.id) continue;
        if (
          court.ownerId === currentUid ||
          (court as any).arenaOwnerId === currentUid ||
          (court as any).ownerUid === currentUid ||
          (court as any).arenaId === currentUid ||
          court.userId === currentUid ||
          court.id === `virtual_arena_${currentUid}` ||
          ownerKeys.has(court.ownerId || "")
        ) {
          courtIds.add(court.id);
        }
      }

      const courtIdsList = Array.from(courtIds).filter(Boolean);
      for (let i = 0; i < courtIdsList.length; i += 10) {
        const chunk = courtIdsList.slice(i, i + 10);
        const qCourt = query(collection(db, "bookings"), where("courtId", "in", chunk));
        const sCourt = await getDocs(qCourt).catch(() => null);
        if (sCourt && !sCourt.empty) {
          sCourt.docs.forEach((d) => {
            const data = d.data();
            if (isRealCourtBookingDocument(data, d.id)) {
              allDocsMap.set(d.id, { id: d.id, ...data });
            }
          });
        }
      }

      return Array.from(allDocsMap.values());
    } catch (err) {
      console.warn("Client booking reconciliation warning:", err);
      return [];
    } finally {
      inFlightReconciliations.delete(inFlightKey);
    }
  })();

  inFlightReconciliations.set(inFlightKey, reconciliationPromise);
  return reconciliationPromise;
}

// --- INTEGRAÇÃO DE CORTESIAS E AUDITORIA COMERCIAL NO FIRESTORE ---

export async function saveCommercialCourtesiesFirestore(courtesies: any[]) {
  if (!isFirebaseConfigured()) return;
  try {
    const docRef = doc(db, "system_settings", "commercial_courtesies");
    const sanitized = sanitizeForFirestore({ list: courtesies, updatedAt: new Date().toISOString() });
    await setDoc(docRef, sanitized, { merge: true });
  } catch (error) {
    console.error("Erro ao salvar cortesias comerciais no Firestore:", error);
  }
}

export async function loadCommercialCourtesiesFirestore(): Promise<any[] | null> {
  if (!isFirebaseConfigured()) return null;
  try {
    const docRef = doc(db, "system_settings", "commercial_courtesies");
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      return snap.data()?.list || [];
    }
  } catch (error) {
    console.error("Erro ao carregar cortesias comerciais do Firestore:", error);
  }
  return null;
}

export function subscribeCommercialCourtesiesFirestore(callback: (courtesies: any[]) => void) {
  if (!isFirebaseConfigured()) return () => {};
  try {
    const docRef = doc(db, "system_settings", "commercial_courtesies");
    return onSnapshot(
      docRef,
      (snap) => {
        if (snap.exists()) {
          callback(snap.data()?.list || []);
        } else {
          callback([]);
        }
      },
      (err) => {
        console.error("Erro na escuta de cortesias comerciais:", err);
      }
    );
  } catch (err) {
    console.error("Erro ao registrar observador de cortesias comerciais:", err);
    return () => {};
  }
}

export async function saveCommercialAuditLogsFirestore(logs: any[]) {
  if (!isFirebaseConfigured()) return;
  try {
    const docRef = doc(db, "system_settings", "commercial_audit_logs");
    const sanitized = sanitizeForFirestore({ list: logs.slice(0, 100), updatedAt: new Date().toISOString() });
    await setDoc(docRef, sanitized, { merge: true });
  } catch (error) {
    console.error("Erro ao salvar logs de auditoria comercial no Firestore:", error);
  }
}
